import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ParticipantName } from './BracketTreeView'

/* Free-For-All heats view — matches per ronde als heat-blokken.
   Per heat toont deelnemers + hun individuele score uit deathmatch_scores.
   Cumulatief klassement bovenaan. */
export default function FFABracket({ matches, participantMap, participantHrefs = {}, onMatchClick, isAdmin }) {
  const byRound = useMemo(() => {
    const g = {}
    matches.forEach(m => { (g[m.round] ||= []).push(m) })
    return g
  }, [matches])
  const rounds = Object.keys(byRound).map(Number).sort((a, b) => a - b)

  // Cumulatief klassement — som van score-veld uit deathmatch_scores per deelnemer.
  // Backend stuurt deathmatch_scores mee in de match als m.deathmatch_scores?
  // Voor MVP: som gewoon score veld als aanwezig.
  const overall = useMemo(() => {
    const tot = new Map()
    matches.forEach(m => {
      (m.deathmatch_scores || []).forEach(s => {
        const cur = tot.get(s.participant_id) || { participant_id: s.participant_id, score: 0, played: 0 }
        cur.score += Number(s.score || 0)
        cur.played += 1
        tot.set(s.participant_id, cur)
      })
    })
    return Array.from(tot.values()).sort((a, b) => b.score - a.score)
  }, [matches])

  return (
    <div className="space-y-6">
      {overall.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-acid">
              <Trophy className="h-4 w-4" /> Klassement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border/40">
                  <th className="text-left py-1 pr-2 w-10">#</th>
                  <th className="text-left py-1 pr-2">Deelnemer</th>
                  <th className="text-right py-1 px-2 w-16">Heats</th>
                  <th className="text-right py-1 pl-2 w-20">Totaal</th>
                </tr>
              </thead>
              <tbody>
                {overall.map((r, i) => (
                  <tr key={r.participant_id} className="border-b border-border/20 last:border-0">
                    <td className="py-1 pr-2 font-mono text-xs">{i + 1}</td>
                    <td className="py-1 pr-2 font-semibold truncate max-w-[240px]">
                      <ParticipantName name={participantMap[r.participant_id] || `#${r.participant_id}`}
                                       href={participantHrefs[r.participant_id]} />
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums text-muted-foreground">{r.played}</td>
                    <td className="py-1 pl-2 text-right tabular-nums font-bold text-acid">{r.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {rounds.map(r => (
        <Card key={r}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-widest">
              Ronde {r} — {byRound[r].length} heat{byRound[r].length === 1 ? '' : 's'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {byRound[r].map(m => (
                <HeatCard key={m.id} match={m} participantMap={participantMap} participantHrefs={participantHrefs}
                          onClick={() => isAdmin && onMatchClick && onMatchClick(m)}
                          clickable={isAdmin} />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function HeatCard({ match, participantMap, participantHrefs = {}, onClick, clickable }) {
  const scores = (match.deathmatch_scores || []).slice()
    .sort((a, b) => (a.placement || 999) - (b.placement || 999) || (b.score || 0) - (a.score || 0))
  const done = match.status === 'completed'
  return (
    <div onClick={clickable ? onClick : undefined}
         className={cn('rounded-md border border-border p-3 space-y-1',
                       clickable && 'cursor-pointer hover:border-acid transition-colors',
                       done && 'border-live/30')}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          Heat {match.group_number || match.match_number}
        </div>
        <div className={cn('text-[10px] font-mono uppercase tracking-widest',
                           done ? 'text-live' : 'text-muted-foreground')}>
          {done ? 'Klaar' : match.status === 'in_progress' ? 'Bezig' : 'Wacht'}
        </div>
      </div>
      <div className="space-y-1">
        {scores.length ? scores.map((s, i) => (
          <div key={s.participant_id}
               className={cn('flex items-center gap-2 px-2 py-1 rounded text-sm',
                             i === 0 && done && 'bg-live/15 text-live font-semibold')}>
            <span className="w-5 text-xs font-mono text-muted-foreground">{s.placement || i + 1}</span>
            <ParticipantName className="flex-1 truncate"
                             name={participantMap[s.participant_id] || `#${s.participant_id}`}
                             href={participantHrefs[s.participant_id]} />
            <span className="font-mono tabular-nums font-bold">{s.score || 0}</span>
          </div>
        )) : (
          <div className="text-xs text-muted-foreground italic">Nog geen scores</div>
        )}
      </div>
    </div>
  )
}
